import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { normalizePhoneNumberForParsing } from './formatPhoneNumber';

/**
 * Creates a phone number onChange handler for MuiTelInput components.
 * This function handles:
 * - Parsing valid phone numbers and returning E.164 format
 * - Preserving national number when only the country changes
 * - Normalizing invalid or incomplete phone numbers
 * 
 * @param onChange - The field's onChange callback from react-hook-form
 * @param currentValue - The current field value
 * @returns An onChange handler function compatible with MuiTelInput
 */
export const createPhoneNumberHandler = (
  onChange: (value: string) => void,
  currentValue?: string
) => {
  return (value: string, info: any) => {
    // Normalize value by ensuring it starts with "+" if it contains digits
    const normalizedValue = normalizePhoneNumberForParsing(value);

    // Normalize currentValue similarly if provided
    const normalizedCurrentValue = normalizePhoneNumberForParsing(currentValue);

    // Handle same country calling code check (for EmployeeRegister compatibility)
    if (info && info.reason === "country" && info.countryCallingCode && normalizedCurrentValue) {
      const currentParsed = parsePhoneNumberFromString(normalizedCurrentValue);
      const currentCallingCode = currentParsed?.countryCallingCode || null;
      if (currentCallingCode && info.countryCallingCode === currentCallingCode) {
        onChange(currentValue || "");
        return;
      }
    }

    // Try to parse the phone number
    const parsed = parsePhoneNumberFromString(normalizedValue);
    if (parsed) {
      onChange(parsed.number);
      return;
    }

    // Preserve national number when only the country changes
    if (info && info.reason === 'country' && info.countryCallingCode && normalizedCurrentValue) {
      const oldParsed = parsePhoneNumberFromString(normalizedCurrentValue);
      const oldNational = oldParsed 
        ? oldParsed.nationalNumber 
        : normalizedCurrentValue.replace(/[^\d]/g, "");
      const newNumber = `+${info.countryCallingCode}${oldNational}`;
      onChange(newNumber);
      return;
    }

    // Normalize invalid or incomplete phone numbers
    const cleaned = normalizedValue.replace(/[^\d+]/g, "");
    let finalNormalized = "";
    if (cleaned.startsWith("+")) {
      finalNormalized = cleaned;
    } else if (cleaned) {
      finalNormalized = "+" + cleaned;
    }
    onChange(finalNormalized);
  };
};

